// 8 种已知 block body schema —— 与 Rust / Go 端字段同构

import { z } from "zod";

// ---------- text ----------
export const TextBodySchema = z.object({
  content: z.string(),
  mentions: z.array(z.string()).default([]),
});
export type TextBody = z.infer<typeof TextBodySchema>;

// ---------- terminal-stream ----------
export const TerminalEncodingSchema = z.enum(["utf8", "base64"]);
export type TerminalEncoding = z.infer<typeof TerminalEncodingSchema>;

export const TerminalStreamBodySchema = z.object({
  session_id: z.string(),
  chunk_index: z.number().int().nonnegative(),
  encoding: TerminalEncodingSchema,
  data: z.string(),
  eof: z.boolean(),
});
export type TerminalStreamBody = z.infer<typeof TerminalStreamBodySchema>;

// ---------- code-diff ----------
export const CodeDiffBodySchema = z.object({
  file_path: z.string(),
  lang: z.string(),
  old_sha: z.string().optional(),
  new_sha: z.string().optional(),
  unified: z.string(),
});
export type CodeDiffBody = z.infer<typeof CodeDiffBodySchema>;

// ---------- approval-card ----------
export const RiskLevelSchema = z.enum(["low", "medium", "high", "critical"]);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const ApprovalStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
  "expired",
]);
export type ApprovalStatus = z.infer<typeof ApprovalStatusSchema>;

export const ApprovalCardBodySchema = z.object({
  request_id: z.string(),
  agent_id: z.string(),
  command: z.string(),
  risk_level: RiskLevelSchema,
  context: z.string().optional(),
  expires_at: z.number().int().nonnegative(),
  status: ApprovalStatusSchema,
  approved_by: z.string().optional(),
});
export type ApprovalCardBody = z.infer<typeof ApprovalCardBodySchema>;

// ---------- embed-html ----------
export const EmbedHtmlBodySchema = z.object({
  title: z.string(),
  url: z.string().optional(),
  raw_html: z.string().optional(),
  sandboxed: z.boolean(),
});
export type EmbedHtmlBody = z.infer<typeof EmbedHtmlBodySchema>;

// ---------- task-card ----------
export const TaskStatusSchema = z.enum([
  "todo",
  "running",
  "done",
  "failed",
  "cancelled",
]);
export type TaskStatus = z.infer<typeof TaskStatusSchema>;

export const TaskStepStatusSchema = z.enum(["pending", "done", "failed"]);
export type TaskStepStatus = z.infer<typeof TaskStepStatusSchema>;

export const TaskStepSchema = z.object({
  label: z.string(),
  status: TaskStepStatusSchema,
});
export type TaskStep = z.infer<typeof TaskStepSchema>;

export const TaskCardBodySchema = z.object({
  task_id: z.string(),
  title: z.string(),
  status: TaskStatusSchema,
  agent_id: z.string().optional(),
  steps: z.array(TaskStepSchema).default([]),
  result_block_id: z.string().optional(),
});
export type TaskCardBody = z.infer<typeof TaskCardBodySchema>;

// ---------- file-card ----------
export const FileCardBodySchema = z.object({
  file_id: z.string(),
  name: z.string(),
  size: z.number().int().nonnegative(),
  mime: z.string(),
  sha256: z.string(),
  url: z.string().optional(),
});
export type FileCardBody = z.infer<typeof FileCardBodySchema>;

// ---------- x-custom ----------
export const XCustomBodySchema = z.object({
  /** 自定义 schema 标识，例 "vendor.foo.bar/v1" */
  schema: z.string(),
  data: z.unknown(),
});
export type XCustomBody = z.infer<typeof XCustomBodySchema>;

// ---------- registry for routing ----------
export const BODY_SCHEMAS = {
  text: TextBodySchema,
  "terminal-stream": TerminalStreamBodySchema,
  "code-diff": CodeDiffBodySchema,
  "approval-card": ApprovalCardBodySchema,
  "embed-html": EmbedHtmlBodySchema,
  "task-card": TaskCardBodySchema,
  "file-card": FileCardBodySchema,
  "x-custom": XCustomBodySchema,
} as const;
